/**
 * Focus Trap Utility
 * 
 * A reusable utility to trap focus within a specific container element.
 * Useful for modals, dropdowns, and other overlay components.
 * 
 * Usage:
 * const focusTrap = new FocusTrap(containerElement);
 * focusTrap.activate();
 * focusTrap.deactivate();
 */

class FocusTrap {
  constructor(container) {
    this.container = container;
    this.isActive = false;
    this.previouslyFocusedElement = null;
    
    // Bind methods to maintain context
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
  }

  /**
   * Get all focusable elements within the container
   * @returns {NodeList} List of focusable elements
   */
  getFocusableElements() {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');

    return this.container.querySelectorAll(focusableSelectors);
  }

  /**
   * Get the first focusable element in the container
   * @returns {Element|null} First focusable element or null
   */
  getFirstFocusableElement() {
    const focusableElements = this.getFocusableElements();
    return focusableElements.length > 0 ? focusableElements[0] : null;
  }

  /**
   * Get the last focusable element in the container
   * @returns {Element|null} Last focusable element or null
   */
  getLastFocusableElement() {
    const focusableElements = this.getFocusableElements();
    return focusableElements.length > 0 ? focusableElements[focusableElements.length - 1] : null;
  }

  /**
   * Handle keydown events to trap focus
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!this.isActive) return;

    const { key, shiftKey } = event;
    
    // Only handle Tab key
    if (key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // If no focusable elements, prevent default
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    // If only one focusable element, prevent tabbing
    if (focusableElements.length === 1) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
      return;
    }

    // Handle Shift+Tab (backward)
    if (shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus({ preventScroll: true });
      }
    } 
    // Handle Tab (forward)
    else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus({ preventScroll: true });
      }
    }
  }

  /**
   * Handle focus events to ensure focus stays within container
   * @param {FocusEvent} event - The focus event
   */
  handleFocusIn(event) {
    if (!this.isActive) return;

    // If focus is outside the container, move it back inside
    if (!this.container.contains(event.target)) {
      event.preventDefault();
      const firstElement = this.getFirstFocusableElement();
      if (firstElement) {
        firstElement.focus({ preventScroll: true });
      }
    }
  }

  /**
   * Activate the focus trap
   */
  activate() {
    if (this.isActive) return;

    this.isActive = true;
    
    // Store the currently focused element to restore later
    this.previouslyFocusedElement = document.activeElement;

    // Add event listeners
    document.addEventListener('keydown', this.handleKeyDown, true);
    document.addEventListener('focusin', this.handleFocusIn, true);

    // Focus the first focusable element
    const firstElement = this.getFirstFocusableElement();
    if (firstElement) {
      // Use setTimeout to ensure the element is ready to receive focus
      setTimeout(() => {
        firstElement.focus({ preventScroll: true });
      }, 0);
    }
  }

  /**
   * Deactivate the focus trap
   */
  deactivate() {
    if (!this.isActive) return;

    this.isActive = false;

    // Remove event listeners
    document.removeEventListener('keydown', this.handleKeyDown, true);
    document.removeEventListener('focusin', this.handleFocusIn, true);

    // Restore focus to the previously focused element without scrolling
    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
      this.previouslyFocusedElement.focus({ preventScroll: true });
    }

    this.previouslyFocusedElement = null;
  }

  /**
   * Check if the focus trap is currently active
   * @returns {boolean} True if active, false otherwise
   */
  isActivated() {
    return this.isActive;
  }

  /**
   * Destroy the focus trap and clean up
   */
  destroy() {
    this.deactivate();
    this.container = null;
  }
}

export default FocusTrap;
